import { Injectable } from '@angular/core';
import { ScrollVideo } from '@pcp/core/interfaces';
import { scrollVideoFrames, scrollVideoFrameUrl, scrollVideoSeedFrame } from '@pcp/core/util';

/** Frames loading concurrently per slide — smooths the burst instead of firing ~140 requests at once. */
const POOL_SIZE = 6;

/** One preloaded frame set, refcounted across the layer containers that show the slide. */
interface PreloadEntry {
    refCount: number;
    /** Flips true on release — the pool stops launching new loads (in-flight ones just finish). */
    aborted: boolean;
    /** Refs pin the fetched frames for the browser's caches until the entry is released. */
    images: HTMLImageElement[];
}

/**
 * ScrollVideoFramePreloader (`canvas-scroll-video-slides`, design D3 rework) — element-driven
 * frame warming: the fabric slide container calls `retain()` when it shows a scroll-video and
 * `release()` when the slide leaves, so preloading runs on EVERY client that renders the slide
 * (teacher, beamer, live-session moderator, live-lesson student) with zero app wiring. This
 * replaced the prefetch-worker route, whose app-level registration + `@pcp/auth` dependency left
 * the live-lesson apps without preload.
 *
 * Politeness: frames load through a small concurrency pool ({@link POOL_SIZE}) in seed-outward
 * order — the frames nearest the initially shown frame first, so early scrubbing hits warm frames
 * even while the tail is still loading — with `fetchPriority='low'` so the browser deprioritizes
 * them against the slide's own critical assets. Each frame is `decode()`d so the swap avoids the
 * decode hitch too. `crossOrigin='anonymous'` matches the fabric background loader, so the cached
 * responses are reusable by the canvas. URLs come from the shared `scrollVideoFrameUrl` resolver —
 * the same one the display path uses — so preloaded and rendered URLs can never diverge.
 */
@Injectable({ providedIn: 'root' })
export class ScrollVideoFramePreloader {
    private readonly active = new Map<number, PreloadEntry>();

    /**
     * Start (or join) preloading the slide's frame set. Refcounted: a second layer showing the
     * same slide joins the existing set instead of re-fetching.
     */
    retain(slideId: number, scrollVideo: ScrollVideo, sas: string): void {
        const existing = this.active.get(slideId);
        if (existing) {
            existing.refCount++;
            return;
        }
        const entry: PreloadEntry = { refCount: 1, aborted: false, images: [] };
        this.active.set(slideId, entry);
        void this.loadPooled(entry, scrollVideo, sas);
    }

    /** Drop one retention; the last release aborts pending loads and lets the frame refs go. */
    release(slideId: number): void {
        const entry = this.active.get(slideId);
        if (!entry) return;
        entry.refCount--;
        if (entry.refCount > 0) return;
        entry.aborted = true;
        entry.images.length = 0;
        this.active.delete(slideId);
    }

    /**
     * Load every frame through a bounded pool, nearest-to-seed first (scrub locality: the user
     * scrubs outward from the initially shown frame, so those neighbours matter most).
     */
    private async loadPooled(entry: PreloadEntry, scrollVideo: ScrollVideo, sas: string): Promise<void> {
        const seed = scrollVideoSeedFrame(scrollVideo);
        const queue = scrollVideoFrames(scrollVideo)
            .sort((a, b) => Math.abs(a - seed) - Math.abs(b - seed));
        const worker = async (): Promise<void> => {
            for (;;) {
                const frame = queue.shift();
                if (frame === undefined || entry.aborted) return;
                await this.loadFrame(entry, scrollVideoFrameUrl(scrollVideo, frame, sas));
            }
        };
        await Promise.all(Array.from({ length: POOL_SIZE }, () => worker()));
    }

    /** Fetch + decode one frame; a failure (404, eviction, abort) is non-fatal — it loads on demand later. */
    private async loadFrame(entry: PreloadEntry, url: string): Promise<void> {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        // Typed in recent DOM libs only — set defensively; browsers without it just ignore the hint.
        (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'low';
        img.src = url;
        entry.images.push(img);
        await img.decode?.().catch(() => undefined);
    }
}
